import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  TUpdateTicketValidation,
  UpdateTicketValidationSchema,
  useUpdateTicketMutation,
} from '../../../api/update-ticket.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_SINGLE_TICKET_QUERY_KEY } from '../../../api/get-single-ticket.api';
import { GET_TICKET_ACTIVITY_QUERY_KEY } from '../../../../ticket-activity/api/get-list-ticket-activity.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';

export interface ICompleteTicketModalProps {
  spaceId: string;
  ticketId: string;
  onSuccess?: () => Promise<void> | void;
}

export function useCompleteTicketModal({ spaceId, ticketId, onSuccess }: ICompleteTicketModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();

  const mutationUpdateTicket = useUpdateTicketMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error update ticketing ticket, please try again';
        showToast({ message: errorMessage, type: 'error' });
      },
      onSuccess: async () => {
        showToast({ message: 'Ticketing ticket updated successfully!', type: 'success' });
        modalActions.closeModal('CompleteTicketModal');
        form.reset();

        // Invalidate queries
        if (spaceId && ticketId) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [GET_SINGLE_TICKET_QUERY_KEY, { ticketId }],
            }),
            queryClient.invalidateQueries({
              queryKey: [GET_TICKET_ACTIVITY_QUERY_KEY, { ticketId }],
            }),
          ]);
        }

        // Call the onSuccess callback if provided (e.g., to update form in parent modal)
        if (onSuccess) {
          await onSuccess();
        }
      },
    },
  });

  const form = useForm<TUpdateTicketValidation>({
    resolver: zodResolver(UpdateTicketValidationSchema),
    mode: 'onChange',
    defaultValues: {
      spaceId: '',
      ticketId: '',
      spaceColumnId: '',
      position: '',
      title: '',
      startDate: undefined,
      endDate: undefined,
      content: undefined,
      labelTicketIds: [],
      userIds: [],
      caseIds: [],
      attachments: [],
      isCompleted: false,
    },
  });

  // HELPERS
  /*
    Separate loading form and loading mutation
   */
  const isOpen = useMemo(() => {
    return modalActions.checkIsOpenModal('CompleteTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationUpdateTicket.isPending || form.formState.isSubmitting;
  }, [mutationUpdateTicket, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationUpdateTicket.isPending || form.formState.isSubmitting;
  }, [mutationUpdateTicket, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (ticketId) {
      form.reset({
        spaceId: spaceId,
        ticketId: ticketId,
        isCompleted: true, // We mark the ticket as completed
      });
    }
  }, [ticketId, spaceId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('CompleteTicketModal');
  }, [modalActions]);

  const handleCancelModal = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleSubmitForm = useCallback(
    (data: TUpdateTicketValidation) => {
      mutationUpdateTicket.mutate({
        spaceId: spaceId,
        ticketId: data?.ticketId,
        isCompleted: data?.isCompleted,
      });
    },
    [mutationUpdateTicket, spaceId]
  );

  return {
    // FORM
    form,
    isOpen,
    isDisabledForm,
    isLoadingForm,

    // HANDLERS
    handleCancelModal,
    handleCloseModal,
    handleSubmitForm,
  };
}
